import {useMemo} from "react";
import PropTypes from 'prop-types';
import {
    CElementCover,
    CForm,
    CFormGroup,
    CInput,
    CLabel,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CSwitch,
    CTextarea,
    CSelect,
} from "@coreui/react";
import noop from "../../utils/noop";
import { useTranslation } from "react-i18next";
import {Formik, Field } from "formik";
import { FieldErrorMessage } from "../FieldErrorMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsersCog } from "@fortawesome/free-solid-svg-icons";
import { SaveButton, CancelButton} from "../Buttons";
import * as Yup from "yup";
import CommonRecordData from "../CommonRecordData/CommonRecordData";

export const EmployeeFormModal = ({employee, onSaveClick, onCancelClick, show, loading, onClose, areas}) => {
    
    const {t} = useTranslation(["EmployeeFormModal", "Buttons", "Validation"]);
    
    const validationSchema = useMemo( () => Yup.object({
        first_name: Yup.string().required(t("Validation:fieldRequired")),
        last_name: Yup.string().required(t("Validation:fieldRequired")),
        areas_id: Yup.number().moreThan(0,t("Validation:pleaseSelectValue")),
    }),[t]);
    
    return (
        <Formik initialValues={employee}
                validateOnChange
                validateOnMount={parseInt(employee.id) > 0}
                enableReinitialize
                onSubmit={onSaveClick}
                validationSchema={validationSchema}
        >
            {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  resetForm,
                  isValid,
                  setFieldTouched,
                  validateField
                  /* and other goodies */
              }) => {
                
                
                return <CModal show={show} closeOnBackdrop={false} onClose={onClose} size="lg">
                    <CModalHeader closeButton><h2 className="mr-2 d-flex flex-row align-items-center"><FontAwesomeIcon icon={faUsersCog} className="mr-2"/> {t("employeeEdit")}</h2></CModalHeader>
                    <CForm onSubmit={handleSubmit}>
                <CModalBody className="p-4">
                    
                        <CFormGroup>
                            <CLabel htmlFor="first_name">{t("EmployeeFormModal:firstName")}<sup>*</sup></CLabel>
                            <Field name="first_name">
                                {({
                                      field, // { name, value, onChange, onBlur },
                                      meta: {touched, error}
                                  }) =>
                                    <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error}/>
                                }
                            </Field>
                            <FieldErrorMessage name="first_name" />
                        </CFormGroup>
                        <CFormGroup>
                            <CLabel htmlFor="last_name">{t("EmployeeFormModal:lastName")}<sup>*</sup></CLabel>
                            <Field name="last_name">
                                {({
                                      field,                                     
                                      meta: {touched, error}
                                  }) => <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error}/>
                                    
                                }
                            </Field>
                            <FieldErrorMessage name="last_name" />
                        </CFormGroup>
                    
                        <CFormGroup>
                            <CLabel htmlFor="code_r">{t("EmployeeFormModal:codeR")}</CLabel>
                            <Field name="code_r">
                                {({
                                      field,
                                      meta: {touched, error}
                                  }) =>
                                    <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error} />
                                }
                            </Field>
                            <FieldErrorMessage name="code_r" />
                        </CFormGroup>

                        <CFormGroup>
                            <CLabel htmlFor="code_t">{t("EmployeeFormModal:codeT")}</CLabel>
                            <Field name="code_t">
                                {({
                                      field,
                                      meta: {touched, error}
                                  }) =>
                                    <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error} />
                                }
                            </Field>
                            <FieldErrorMessage name="code_t" />
                        </CFormGroup>
                    
                        <CFormGroup>
                            <CLabel htmlFor="phone_number">{t("EmployeeFormModal:phoneNumber")}</CLabel>
                            <Field name="phone_number">
                                {({
                                      field,
                                      meta: {touched, error}
                                  }) =>
                                    <CInput autoComplete="off" {...field} invalid={error && touched} valid={touched && !error} />
                                }
                            </Field>
                            <FieldErrorMessage name="phone_number" />
                        </CFormGroup>
                    
                        <CFormGroup className="d-flex flex-row">
                            <Field type="checkbox" name="is_active">
                                {({field, field: { value }}) =>
                                        <CSwitch id="is_active" shape="pill" color="info" labelOn="✓" labelOff="✕" size="sm"
                                                    variant="opposite"
                                                    {...field} value={value === 0 ? true : false}/>
                                }
                            </Field>
                            <CLabel htmlFor="is_active" className="ml-2 user-select-none">{t("EmployeeFormModal:active")}</CLabel>
                        </CFormGroup>

                        <CFormGroup>
                            <CLabel htmlFor="areas_id">{t("EmployeeFormModal:area")}<sup>*</sup></CLabel>                         
                            <Field name="areas_id">
                                {({
                                      field, field: { value },
                                      meta: {touched, error}
                                  }) =>
    
                                    <CSelect {...field} invalid={error && touched} valid={touched && !error}>
                                        {areas.map( r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                    </CSelect>
    
                                }
                            </Field>
                            <FieldErrorMessage name="areas_id" />
                        </CFormGroup>
                    
                        <CFormGroup>
                            <CLabel htmlFor="description" className="ml-2 user-select-none">{t("EmployeeFormModal:description")}</CLabel>
                            <Field name="description">
                                {({
                                      field
                                  }) => 
                                    <CTextarea {...field} />
                                }
                            </Field>
                        </CFormGroup>
                    
                    {employee.id > 0 && <CommonRecordData item={employee} />}
                    
                </CModalBody>
                <CModalFooter>
                    <CancelButton type="button" onClick={onCancelClick}>{t("Buttons:cancel")}</CancelButton>
                    <SaveButton type="submit" color="primary" disabled={isValid===false}>{t("Buttons:save")}</SaveButton>
                </CModalFooter>
                    </CForm>
                    {loading && <CElementCover/>}
            </CModal>}}
        </Formik>
    );
};

EmployeeFormModal.propTypes = {
    employee: PropTypes.shape({
        is_active: PropTypes.number,
        areas_id: PropTypes.number,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        code_r: PropTypes.string,
        code_t: PropTypes.string,
        phone_number: PropTypes.string,
        description: PropTypes.string
    }),
    areas: PropTypes.array,
    loading: PropTypes.bool,
    onChange: PropTypes.func,
    onClose: PropTypes.func,
    onSaveClick: PropTypes.func,
    onCancelClick: PropTypes.func,
};

EmployeeFormModal.defaultProps = {
    loading: false,
    roles: [],
    onChange: noop,
    onClose: noop,
    onSaveClick: noop,
    onCancelClick: noop,
};

export default EmployeeFormModal;